import { useMemo } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

/**
 * Custom hook for accessing authentication state and user information
 * Provides a cleaner API over raw MSAL hooks
 */
export function useAuth() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  
  const user = useMemo(() => {
    if (!accounts || accounts.length === 0) return null;
    
    const account = accounts[0];
    return {
      name: account.name,
      username: account.username,
      email: account.username,
      idTokenClaims: account.idTokenClaims,
    };
  }, [accounts]);

  const userRoles = useMemo(
    () => instance.getActiveAccount()?.idTokenClaims?.roles || [],
    [instance]
  );

  const hasRole = (role) => {
    return userRoles.includes(role);
  };

  const hasAnyRole = (roles) => {
    return roles.some(role => userRoles.includes(role));
  };

  const hasAllRoles = (roles) => {
    return roles.every(role => userRoles.includes(role));
  };

  const login = async () => {
    try {
      const response = await instance.loginPopup();
      instance.setActiveAccount(response.account);
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    const account = accounts[0];
    instance.logout({ account });
  };

  return {
    isAuthenticated,
    user,
    userRoles,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    login,
    logout,
    instance,
    accounts,
  };
}

export default useAuth;

